<template>
  <div class="card mb-25 border-0 rounded-0 bg-white">
    <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-20">
      <div class="d-flex gap-3 flex-wrap align-items-center">
        <router-link to="/ajouter-assignation" class="btn btn-success text-white">
          <i class="flaticon-plus"></i> Ajouter
        </router-link>

        <!-- Filtre par mandature -->
        <select v-model="mandatureFilter" @change="loadData" class="form-select" style="width: auto;">
          <option :value="null">Toutes les mandatures</option>
          <option v-for="m in mandatures" :key="m.id" :value="m.id">
            {{ m.libelle }}
          </option>
        </select>
      </div>

      <form @submit.prevent="loadData" class="search-box position-relative">
        <input
          v-model="searchTerm"
          @keyup.enter="loadData"
          placeholder="Rechercher organe, poste, membre..."
          class="form-control"
        />
        <button type="submit"><i class="flaticon-search-interface-symbol"></i></button>
      </form>
    </div>

    <div class="card-body p-25">
      <div class="table-responsive">
        <table class="table text-nowrap">
          <thead>
            <tr>
              <th>Mandature</th>
              <th>Organe</th>
              <th>Postes & Membres</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="group in groupedData" :key="`${group.mandatureId}-${group.organeId}`">
              <td><span class="badge bg-primary">{{ group.mandatureLibelle }}</span></td>
              <td class="fw-bold">{{ group.organeNom }}</td>
              <td>
                <ul class="list-unstyled mb-0">
                  <li v-for="p in group.postes" :key="p.id" class="mb-3 pb-2 border-bottom">
                    <strong>{{ p.numeroOrdre }} - {{ p.nomPoste }}</strong>
                    <small v-if="p.arrondissement" class="text-muted ms-2">({{ p.arrondissement.libelle }})</small>
                    <div class="mt-1">
                      <span v-if="p.membre" class="text-success">
                        {{ p.membre.nom }} {{ p.membre.prenom }}
                      </span>
                      <span v-else class="text-danger fst-italic">Aucun membre assigné</span>
                    </div>
                  </li>
                </ul>
              </td>
              <td class="text-center">
                <div class="dropdown">
                  <button class="btn btn-sm btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown">
                    Actions
                  </button>
                  <ul class="dropdown-menu">
                    <li>
                      <router-link
                        :to="{ name: 'modifierAssignation', params: { organeId: group.organeId, mandatureId: group.mandatureId } }"
                        class="dropdown-item">
                        Modifier
                      </router-link>
                    </li>
                    <li>
                      <a href="#" @click.prevent="supprimerGroupe(group)" class="dropdown-item text-danger">
                        Supprimer tout
                      </a>
                    </li>
                  </ul>
                </div>
              </td>
            </tr>
            <tr v-if="groupedData.length === 0">
              <td colspan="4" class="text-center py-5 text-muted">Aucune assignation trouvée</td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationComponent
        :page="page"
        :totalPages="totalPages"
        :totalElements="totalElements"
        :limit="limit"
        @paginate="handlePaginate"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import ApiService from "@/services/ApiService";
import PaginationComponent from "@/components/Utilities/Pagination.vue";
import Swal from "sweetalert2";

export default defineComponent({
  components: { PaginationComponent },
  setup() {
    const groupedData = ref<any[]>([]);
    const mandatures = ref<any[]>([]);
    const page = ref(1);
    const limit = ref(10);
    const totalPages = ref(0);
    const totalElements = ref(0);
    const searchTerm = ref("");
    const mandatureFilter = ref<number | null>(null);

    const loadData = () => {
      ApiService.get("/pmms", {
        params: {
          page: page.value,
          limit: limit.value,
          mot: searchTerm.value,
          mandatureId: mandatureFilter.value
        }
      }).then(({ data }) => {
        groupedData.value = data.data;
        totalPages.value = data.totalPages;
        totalElements.value = data.totalElements;
      });
    };

    const loadMandatures = async () => {
      try {
        const res = await ApiService.get("/mandature");
        mandatures.value = res.data.data.data || [];
      } catch (err) {
        console.error("Erreur chargement mandatures", err);
      }
    };

    onMounted(() => {
      loadMandatures();
      loadData();
    });

    const handlePaginate = ({ page_, limit_ }: any) => {
      page.value = page_;
      limit.value = limit_;
      loadData();
    };

    const supprimerGroupe = (group: any) => {
      Swal.fire({
        title: "Supprimer ?",
        text: `Toutes les assignations de "${group.organeNom}" pour la mandature "${group.mandatureLibelle}" seront supprimées.`,
        icon: "warning",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) {
          ApiService.delete(`/pmms/organe/${group.organeId}/mandature/${group.mandatureId}`)
            .then(() => {
              Swal.fire("Supprimé !", "", "success");
              loadData();
            });
        }
      });
    };

    return {
      groupedData,
      mandatures,
      mandatureFilter,
      page,
      limit,
      totalPages,
      totalElements,
      searchTerm,
      loadData,
      handlePaginate,
      supprimerGroupe
    };
  }
});
</script>